import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { RefreshCw, Radio, Compass, Activity, Power, Wifi, Map, EyeOff, Navigation, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, CheckCircle2, Zap } from 'lucide-react';
import './App.css';

// Math utilities
const deg2rad = (deg) => (deg * Math.PI) / 180;
const rad2deg = (rad) => (rad * 180) / Math.PI;
const SPEED_OF_LIGHT = 299792458; // m/s
const FREQ_2M = 145000000; // 145 MHz
const WAVELENGTH = SPEED_OF_LIGHT / FREQ_2M;
const ARRAY_RADIUS = 0.5 * WAVELENGTH; // 0.5λ radius

function App() {
  const [txPos, setTxPos] = useState({ r: 200, theta: 45 }); // r in px, theta in degrees
  const [playerPos, setPlayerPos] = useState({ x: 0, y: 0 }); // Player position for Fox Hunting modes
  const [activePort, setActivePort] = useState(2); // Port 1 is ref, so multiplexer starts at 2
  const [isCalibrated, setIsCalibrated] = useState(false);
  const [cableDelays, setCableDelays] = useState(Array(8).fill(0));
  const [isDragging, setIsDragging] = useState(false);
  const [estimatedAoA, setEstimatedAoA] = useState(45);
  const [aoaHistory, setAoAHistory] = useState([]); // Track last 30 angles
  const [mode, setMode] = useState(1); // 1 = Free Roam, 2 = AoA Fox Hunt, 3 = RSSI Fox Hunt
  const [urbanMultipath, setUrbanMultipath] = useState(false);
  const [foxFound, setFoxFound] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([{x: 0, y: 0}]);
  const [showAoAInRSSI, setShowAoAInRSSI] = useState(false); // Toggle to peek at AoA in RSSI mode
  const [huntHistory, setHuntHistory] = useState([]); // Track steps of last 15 hunts
  const [currentSteps, setCurrentSteps] = useState(0); // Track steps of current hunt
  const canvasRef = useRef(null);
  
  // Calculate antenna positions
  const numAntennas = 8;
  const antennas = useMemo(() => {
    return Array.from({ length: numAntennas }).map((_, i) => {
      const angle = (i * 360) / numAntennas;
      return {
        id: i + 1,
        angle: angle,
        x: Math.cos(deg2rad(angle)),
        y: Math.sin(deg2rad(angle))
      };
    });
  }, []);

  // Generate a random Fox position
  const hideFox = useCallback(() => {
    const r = 100 + Math.random() * 150; // Random distance
    const theta = Math.random() * 360; // Random angle
    setTxPos({ r, theta });
    setPlayerPos({ x: 0, y: 0 }); // Reset player to center
    setBreadcrumbs([{x: 0, y: 0}]);
    setFoxFound(false);
    setCurrentSteps(0);
  }, []);

  // Mode Switch Handler
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 2 || newMode === 3) {
      hideFox();
    } else {
      setPlayerPos({ x: 0, y: 0 });
      setTxPos({ r: 200, theta: 45 });
      setBreadcrumbs([{x: 0, y: 0}]);
      setFoxFound(false);
      setCurrentSteps(0);
    }
  };

  // Calculate RSSI for each antenna
  const calculateRSSI = useCallback(() => {
    // Relative position of Fox to Player
    const playerX = playerPos.x;
    const playerY = playerPos.y;
    
    const foxX = txPos.r * Math.cos(deg2rad(txPos.theta));
    const foxY = txPos.r * Math.sin(deg2rad(txPos.theta));
    
    const dx = foxX - playerX;
    const dy = foxY - playerY;
    const distanceToFox = Math.sqrt(dx*dx + dy*dy);
    const angleToFox = rad2deg(Math.atan2(dy, dx));
    
    return antennas.map(ant => {
      // Base signal strength drops with distance squared
      let signalStrength = Math.max(0, 100 - (distanceToFox / 5));
      
      // Antenna directionality (slightly higher when facing the source)
      const angleDiff = Math.abs((angleToFox - ant.angle + 360) % 360);
      const normalizedDiff = angleDiff > 180 ? 360 - angleDiff : angleDiff;
      const directionGain = (180 - normalizedDiff) / 180 * 20; // Up to 20 "points" of gain
      
      let rssi = signalStrength + directionGain;
      
      // Simulate Urban Multipath
      if (urbanMultipath) {
        // Add random noise and occasional false peaks
        const noise = (Math.random() - 0.5) * 40; // High noise
        const falsePeak = Math.random() > 0.85 ? 30 : 0; // Occasional strong reflection
        rssi += noise + falsePeak;
      } else {
        // Clean environment
        rssi += (Math.random() - 0.5) * 5; // Slight thermal noise
      }
      
      return Math.min(100, Math.max(0, rssi)); // Clamp 0-100
    });
  }, [playerPos, txPos, antennas, urbanMultipath]);

  const rssiValues = calculateRSSI();

  // Move Player
  const movePlayer = (dirX, dirY) => {
    if (foxFound) return;
    
    const stepSize = 20;
    setPlayerPos(prev => {
      const newPos = {
        x: prev.x + dirX * stepSize,
        y: prev.y + dirY * stepSize
      };
      
      setCurrentSteps(s => s + 1);
      
      // Check if found Fox
      const foxX = txPos.r * Math.cos(deg2rad(txPos.theta));
      const foxY = txPos.r * Math.sin(deg2rad(txPos.theta));
      const dist = Math.sqrt(Math.pow(newPos.x - foxX, 2) + Math.pow(newPos.y - foxY, 2));
      
      if (dist < 30) {
        setFoxFound(true);
        // Save hunt to history
        setHuntHistory(h => {
          const newHistory = [{ mode, steps: currentSteps + 1, multipath: urbanMultipath }, ...h];
          return newHistory.slice(0, 15); // Keep last 15
        });
      }
      
      setBreadcrumbs(b => [...b, newPos]);
      return newPos;
    });
  };
  // Calculate phase differences
  const calculatePhases = useCallback(() => {
    // Reference antenna is RX1 (port 1, angle 0)
    const refPos = antennas[0];
    
    // For modes 2 & 3, calculate based on player relative position to fox
    let angleToFox = txPos.theta;
    if (mode > 1) {
      const playerX = playerPos.x;
      const playerY = playerPos.y;
      const foxX = txPos.r * Math.cos(deg2rad(txPos.theta));
      const foxY = txPos.r * Math.sin(deg2rad(txPos.theta));
      
      const dx = foxX - playerX;
      const dy = foxY - playerY;
      angleToFox = rad2deg(Math.atan2(dy, dx));
      if (angleToFox < 0) angleToFox += 360;
    }
    
    const txAngleRad = deg2rad(angleToFox);
    
    // Calculate phase for each antenna relative to reference
    return antennas.map(ant => {
      // Phase = 2π/λ * d * cos(θ - θ_i)
      let phaseDiff = (2 * Math.PI * ARRAY_RADIUS / WAVELENGTH) * 
        (Math.cos(txAngleRad - deg2rad(ant.angle)) - Math.cos(txAngleRad - deg2rad(refPos.angle)));
      
      // Simulate Urban Multipath effect on phase (less severe than on RSSI)
      if (urbanMultipath && mode > 1) {
        const noise = (Math.random() - 0.5) * 0.1; // Small phase noise
        phaseDiff += noise;
      }
      
      return rad2deg(phaseDiff);
    });
  }, [antennas, txPos, playerPos, mode, urbanMultipath]);

  const phases = calculatePhases();

  // Handle HMC253 Switcher Animation
  useEffect(() => {
    const interval = setInterval(() => {
      // Port 1 is reference (center), so multiplexer cycles through ports 2-9 for the 8 array antennas
      setActivePort(prev => ((prev - 1) % 8) + 2);
    }, 100); // Switch every 100ms
    return () => clearInterval(interval);
  }, []);

  // Update AoA Estimation (simplified MUSIC simulation)
  useEffect(() => {
    // In Fox Hunt modes, AoA is relative to player position
    let trueAngle = txPos.theta;
    if (mode > 1) {
      const dx = txPos.r * Math.cos(deg2rad(txPos.theta)) - playerPos.x;
      const dy = txPos.r * Math.sin(deg2rad(txPos.theta)) - playerPos.y;
      trueAngle = rad2deg(Math.atan2(dy, dx));
      if (trueAngle < 0) trueAngle += 360;
    }

    const baseJitter = isCalibrated ? 2 : 10;
    const multipathJitter = (urbanMultipath && mode > 1) ? 5 : 0; // Phase is resilient but not immune
    const totalJitter = (Math.random() - 0.5) * (baseJitter + multipathJitter);
    
    const newAoA = (trueAngle + totalJitter + 360) % 360;
    setEstimatedAoA(newAoA);
    
    // Update history
    setAoAHistory(prev => {
      const newHistory = [...prev, newAoA];
      if (newHistory.length > 30) {
        return newHistory.slice(newHistory.length - 30);
      }
      return newHistory;
    });
  }, [txPos.theta, txPos.r, isCalibrated, activePort, mode, playerPos, urbanMultipath]);

  // Calculate most frequent angle (mode) in history (bucketed by degrees)
  const modeAoA = useMemo(() => {
    if (aoaHistory.length === 0) return estimatedAoA;
    
    // Bucket angles into 1-degree bins
    const counts = {};
    let maxCount = 0;
    let mode = Math.round(estimatedAoA);
    
    aoaHistory.forEach(angle => {
      const rounded = Math.round(angle);
      counts[rounded] = (counts[rounded] || 0) + 1;
      if (counts[rounded] > maxCount) {
        maxCount = counts[rounded];
        mode = rounded;
      }
    });
    
    return mode;
  }, [aoaHistory, estimatedAoA]);

  // Handle Canvas Interaction
  const handleCanvasMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    const r = Math.sqrt(x*x + y*y);
    if (Math.abs(r - txPos.r) < 20) {
      setIsDragging(true);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDragging || mode !== 1) return; // Only allow drag in Mode 1
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    let theta = rad2deg(Math.atan2(y, x));
    if (theta < 0) theta += 360;
    
    const r = Math.max(Math.sqrt(x*x + y*y), 50); // Allow placing anywhere outside center
    
    setTxPos({ r, theta });
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
  };

  const handleZeroing = () => {
    setIsCalibrated(false);
    // Simulate calibration process
    setTimeout(() => {
      setCableDelays(Array(8).fill(0).map(() => (Math.random() - 0.5) * 10)); // random delays in degrees
      setIsCalibrated(true);
    }, 1500);
  };

  // Draw Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;
    const arrayRadiusPx = 80;

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Draw Grid
    ctx.strokeStyle = 'rgba(57, 255, 20, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= width; i += 20) {
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, height); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(width, i); ctx.stroke();
    }

    // Draw Array Circle
    ctx.beginPath();
    ctx.arc(cx, cy, arrayRadiusPx, 0, 2 * Math.PI);
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw Antennas (8 in circular array)
    antennas.forEach((ant) => {
      const ax = cx + ant.x * arrayRadiusPx;
      const ay = cy + ant.y * arrayRadiusPx;
      
      const isActive = ant.id === activePort - 1; // Adjust for 1-indexed to 0-indexed port mapping

      // Antenna glow if active
      if (isActive) {
        ctx.beginPath();
        ctx.arc(ax, ay, 12, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(57, 255, 20, 0.3)';
        ctx.fill();
      }

      // Antenna point
      ctx.beginPath();
      ctx.arc(ax, ay, 6, 0, 2 * Math.PI);
      ctx.fillStyle = isActive ? '#39ff14' : '#1e293b';
      ctx.strokeStyle = isActive ? '#39ff14' : '#94a3b8';
      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();

      // Antenna label
      ctx.fillStyle = isActive ? '#39ff14' : '#94a3b8';
      ctx.font = '10px "JetBrains Mono"';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`A${ant.id + 1}`, ax, ay - 15);
    });

    // Draw Reference Antenna (Center)
    ctx.beginPath();
    ctx.arc(cx, cy, 12, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(6, 182, 212, 0.3)';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, 2 * Math.PI);
    ctx.fillStyle = '#06b6d4';
    ctx.strokeStyle = '#06b6d4';
    ctx.lineWidth = 2;
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#06b6d4';
    ctx.font = '10px "JetBrains Mono"';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('A1(Ref)', cx + 25, cy + 15);

    // Draw history of AoA (Only if not in RSSI mode, or if peeking)
    if (aoaHistory.length > 0 && (mode !== 3 || showAoAInRSSI)) {
      // Draw all history lines in gray
      ctx.lineWidth = 1;
      aoaHistory.forEach(angle => {
        const angleRad = deg2rad(angle);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angleRad) * 250, cy + Math.sin(angleRad) * 250);
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.2)'; // Gray with low opacity
        ctx.stroke();
      });

      // Draw mode (highest frequency) angle in green
      const modeRad = deg2rad(modeAoA);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(modeRad) * 250, cy + Math.sin(modeRad) * 250);
      ctx.strokeStyle = 'rgba(57, 255, 20, 0.6)'; // Lime green
      ctx.stroke();
    }

    // Draw latest MUSIC AoA estimate line in white (Only if not in RSSI mode, or if peeking)
    if (mode !== 3 || showAoAInRSSI) {
      const aoaRad = deg2rad(estimatedAoA);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(aoaRad) * 250, cy + Math.sin(aoaRad) * 250);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'; // White
      ctx.lineWidth = 2;
      ctx.setLineDash([10, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw Fox in Mode 1 or if Found
    if (mode === 1 || foxFound) {
      const txX = cx + txPos.r * Math.cos(deg2rad(txPos.theta)) - playerPos.x;
      const txY = cy + txPos.r * Math.sin(deg2rad(txPos.theta)) - playerPos.y;

      // Signal waves
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(txX, txY, 15 + (Date.now() / 20 % 30) + i * 10, 0, 2 * Math.PI);
        ctx.strokeStyle = `rgba(239, 68, 68, ${0.5 - i * 0.15})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // TX point
      ctx.beginPath();
      ctx.arc(txX, txY, 8, 0, 2 * Math.PI);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      
      // TX Label
      ctx.fillStyle = '#ef4444';
      ctx.font = '12px "JetBrains Mono"';
      ctx.fillText('TX', txX, txY - 20);
    }

    // Draw Breadcrumbs and Player in Fox Hunt Modes
    if (mode > 1) {
      // Draw breadcrumb trail
      if (breadcrumbs.length > 1) {
        ctx.beginPath();
        ctx.moveTo(cx - playerPos.x + breadcrumbs[0].x, cy - playerPos.y + breadcrumbs[0].y);
        for (let i = 1; i < breadcrumbs.length; i++) {
          ctx.lineTo(cx - playerPos.x + breadcrumbs[i].x, cy - playerPos.y + breadcrumbs[i].y);
        }
        ctx.strokeStyle = mode === 2 ? '#39ff14' : '#ef4444'; // Green for AoA, Red for RSSI
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw Player
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, 2 * Math.PI);
      ctx.fillStyle = '#3b82f6'; // Blue
      ctx.fill();
      ctx.fillStyle = '#3b82f6';
      ctx.fillText('YOU', cx, cy + 20);
    }

  }, [txPos, activePort, antennas, aoaHistory, estimatedAoA, modeAoA, mode, showAoAInRSSI, foxFound, playerPos.x, playerPos.y, breadcrumbs]);

  return (
    <div className="min-h-screen bg-rf-darker text-rf-text font-mono p-4 relative overflow-hidden">
      {/* Scanline Effect */}
      <div className="scanlines absolute inset-0 pointer-events-none opacity-20"></div>

      {/* Header */}
      <header className="flex items-center justify-between border-b border-rf-lime/20 pb-4 mb-6">
        <div className="flex items-center gap-3">
          <Activity className="text-rf-lime animate-pulse" />
          <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-rf-lime to-rf-cyan bg-clip-text text-transparent">
            Jojo-Kraken Phase Simulator
          </h1>
        </div>
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rf-cyan animate-pulse"></span>
            RX1: Ref Channel
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rf-lime animate-pulse"></span>
            RX2: Multiplexed
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Canvas Area */}
        <div className="lg:col-span-2 bg-rf-card/50 border border-rf-lime/20 rounded-lg p-4 relative backdrop-blur-sm">
          <div className="absolute top-4 left-4 flex gap-4 z-10 items-center">
            <div className="flex gap-2">
              <button 
                onClick={() => handleModeChange(1)}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${mode === 1 ? 'bg-rf-lime text-rf-dark' : 'bg-rf-dark border border-rf-lime/50 text-rf-lime'}`}
              >
                M1: Free Roam
              </button>
              <button 
                onClick={() => handleModeChange(2)}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${mode === 2 ? 'bg-rf-lime text-rf-dark' : 'bg-rf-dark border border-rf-lime/50 text-rf-lime'}`}
              >
                M2: AoA Hunt
              </button>
              <button 
                onClick={() => handleModeChange(3)}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${mode === 3 ? 'bg-rf-lime text-rf-dark' : 'bg-rf-dark border border-rf-lime/50 text-rf-lime'}`}
              >
                M3: RSSI Hunt
              </button>
            </div>
            
            <button 
              onClick={() => setUrbanMultipath(!urbanMultipath)}
              className={`flex items-center gap-1 px-3 py-1 rounded text-xs border transition-colors ${urbanMultipath ? 'bg-rf-red text-white border-rf-red' : 'bg-rf-dark text-rf-muted border-rf-muted'}`}
            >
              <Zap size={12} /> Urban Multipath
            </button>
          </div>
          
          <div className="flex justify-center items-center h-[500px]">
            <canvas
              ref={canvasRef}
              width={600}
              height={500}
              className={`${mode === 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-crosshair'}`}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
            />
          </div>
          
          {/* Game Controls */}
          {mode > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-rf-dark/90 p-4 rounded-lg border border-rf-lime/30 flex flex-col items-center gap-2 z-10">
              <div className="text-xs text-rf-lime font-bold mb-2">
                {foxFound ? '🎉 FOX FOUND!' : 'Navigate Array'}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div></div>
                <button onClick={() => movePlayer(0, -1)} className="p-2 bg-rf-card rounded hover:bg-rf-lime/20 border border-rf-lime/50"><ChevronUp size={20}/></button>
                <div></div>
                <button onClick={() => movePlayer(-1, 0)} className="p-2 bg-rf-card rounded hover:bg-rf-lime/20 border border-rf-lime/50"><ChevronLeft size={20}/></button>
                <button onClick={() => hideFox()} className="p-2 bg-rf-card rounded hover:bg-rf-lime text-rf-lime hover:text-rf-dark border border-rf-lime font-bold text-xs"><RefreshCw size={20}/></button>
                <button onClick={() => movePlayer(1, 0)} className="p-2 bg-rf-card rounded hover:bg-rf-lime/20 border border-rf-lime/50"><ChevronRight size={20}/></button>
                <div></div>
                <button onClick={() => movePlayer(0, 1)} className="p-2 bg-rf-card rounded hover:bg-rf-lime/20 border border-rf-lime/50"><ChevronDown size={20}/></button>
                <div></div>
              </div>
            </div>
          )}
        </div>

        {/* Dashboard Panels */}
        <div className="space-y-6">
          {/* Hunt History Panel */}
          {mode > 1 && (
            <div className="bg-rf-card/80 border border-rf-lime/20 rounded-lg p-4 font-mono text-sm shadow-lg shadow-rf-lime/5">
              <h3 className="text-rf-lime flex items-center gap-2 mb-4 border-b border-rf-lime/20 pb-2">
                <Map size={16} /> Hunt History & Stats
              </h3>
              <div className="flex justify-between items-center mb-4">
                <span className="text-rf-muted">Current Hunt Steps:</span>
                <span className={`text-xl font-bold ${foxFound ? 'text-rf-lime' : 'text-white'}`}>{currentSteps}</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-rf-muted mb-1 text-xs">
                  <span>Hunt</span>
                  <span>Mode</span>
                  <span>Steps</span>
                </div>
                {huntHistory.length === 0 ? (
                  <div className="text-center text-rf-muted text-xs italic py-2">No hunts completed yet</div>
                ) : (
                  huntHistory.map((hunt, i) => (
                    <div key={i} className="flex justify-between items-center p-1 rounded bg-rf-dark/50 border border-rf-lime/10 text-xs">
                      <span className="text-rf-muted">#{huntHistory.length - i}</span>
                      <span className={hunt.mode === 2 ? 'text-rf-lime' : 'text-rf-red'}>
                        {hunt.mode === 2 ? 'AoA' : 'RSSI'} 
                        {hunt.multipath && ' (Urban)'}
                      </span>
                      <span className="font-bold text-white">{hunt.steps}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {mode !== 3 ? (
            /* Phase Telemetry (Modes 1 & 2) */
            <div className="bg-rf-card/80 border border-rf-lime/20 rounded-lg p-4 font-mono text-sm shadow-lg shadow-rf-lime/5">
              <h3 className="text-rf-lime flex items-center gap-2 mb-4 border-b border-rf-lime/20 pb-2">
                <Radio size={16} /> Phase Telemetry ($\Delta\phi$)
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-rf-muted mb-1">
                  <span>Port</span>
                  <span>$\Delta\phi$ (deg)</span>
                  <span>Delay</span>
                </div>
                {phases.map((phase, i) => (
                  <div key={i} 
                       className={`flex justify-between items-center p-1 rounded transition-colors
                         ${activePort === i + 2 ? 'bg-rf-lime/10 text-rf-lime' : ''}`}
                  >
                    <span>A{i + 2}</span>
                    <span className="font-bold">{phase.toFixed(1)}°</span>
                    <span className="text-xs text-rf-muted">
                      {cableDelays[i] ? `+${cableDelays[i].toFixed(1)}°` : '--'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* RSSI Telemetry (Mode 3) */
            <div className="bg-rf-card/80 border border-rf-red/20 rounded-lg p-4 font-mono text-sm shadow-lg shadow-rf-red/5">
              <h3 className="text-rf-red flex items-center gap-2 mb-4 border-b border-rf-red/20 pb-2">
                <Activity size={16} /> Legacy RSSI Signal Strength
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-rf-muted mb-1">
                  <span>Antenna</span>
                  <span>Signal Level</span>
                </div>
                {rssiValues.map((rssi, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <div className="flex justify-between text-xs">
                      <span>A{i + 2} ({Math.round(antennas[i].angle)}°)</span>
                      <span className={rssi > 80 ? 'text-rf-red' : 'text-rf-muted'}>{rssi.toFixed(0)}</span>
                    </div>
                    <div className="h-2 w-full bg-rf-dark rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${rssi > 80 ? 'bg-rf-red' : rssi > 50 ? 'bg-rf-lime' : 'bg-rf-cyan'}`}
                        style={{ width: `${rssi}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HMC253 Status */}
          <div className="bg-rf-card/80 border border-rf-lime/20 rounded-lg p-4 shadow-lg shadow-rf-lime/5">
            <div className="flex justify-between items-center mb-4 border-b border-rf-lime/20 pb-2">
              <h3 className="text-rf-lime flex items-center gap-2">
                <Power size={16} /> HMC253 Switcher
              </h3>
              <div className="flex gap-1">
                {[0,1,2].map(bit => (
                  <div key={bit} className={`w-3 h-3 rounded-full border border-rf-lime/50
                    ${(activePort - 1) & (1 << bit) ? 'bg-rf-lime' : 'bg-rf-dark'}`} />
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-rf-muted">Active Port:</span>
              <span className="text-2xl font-bold text-rf-lime">CH{activePort}</span>
            </div>
            <div className="mt-4">
              <div className="h-1 w-full bg-rf-dark rounded-full overflow-hidden">
                <div 
                  className="h-full bg-rf-lime transition-all duration-100"
                  style={{ width: `${((activePort - 1) / 8) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* System Control & Compass */}
          <div className="grid grid-cols-2 gap-4">
            {/* Calibration Panel */}
            <div className="bg-rf-card/80 border border-rf-lime/20 rounded-lg p-4 flex flex-col items-center justify-center gap-3">
              <button 
                onClick={handleZeroing}
                className="w-full py-2 px-4 rounded bg-rf-dark border border-rf-lime text-rf-lime hover:bg-rf-lime hover:text-rf-dark transition-all flex items-center justify-center gap-2 font-bold"
              >
                <RefreshCw size={16} className={!isCalibrated && !cableDelays.every(d => d===0) ? 'animate-spin' : ''} />
                ZERO Array
              </button>
              <div className="text-xs text-center text-rf-muted">
                Status: {isCalibrated ? 
                  <span className="text-rf-lime">Calibrated</span> : 
                  <span className="text-rf-red">Uncalibrated</span>}
              </div>
            </div>

            {/* ESP32 e-Ink Mockup or RSSI Alternative */}
            {mode !== 3 || showAoAInRSSI ? (
              <div className="bg-white rounded-lg p-2 flex flex-col items-center border-4 border-gray-300 shadow-inner relative">
                <div className="absolute top-1 left-1 flex gap-1">
                  <Wifi size={10} className="text-gray-400" />
                </div>
                <Compass size={32} className="text-black mb-1" />
                <div className="text-3xl font-bold text-black font-sans tracking-tighter">
                  {estimatedAoA.toFixed(0)}°
                </div>
                <div className="text-[10px] text-gray-500 font-sans font-bold uppercase mt-1 text-center">
                  MUSIC AoA EST
                  {mode === 3 && <div className="text-red-500 font-bold">(PEEK MODE)</div>}
                </div>
              </div>
            ) : (
              <div className="bg-rf-dark border border-rf-red/50 rounded-lg p-2 flex flex-col items-center justify-center text-center">
                <EyeOff size={32} className="text-rf-red mb-2" />
                <div className="text-sm font-bold text-rf-red">AoA DISABLED</div>
                <div className="text-[10px] text-rf-muted mt-1 mb-2">
                  Use RSSI Bars to Hunt
                </div>
                <button 
                  onClick={() => setShowAoAInRSSI(true)}
                  className="px-2 py-1 text-[10px] bg-rf-card border border-rf-red text-rf-red rounded hover:bg-rf-red hover:text-white transition-colors"
                >
                  PEEK AoA
                </button>
              </div>
            )}
            
            {mode === 3 && showAoAInRSSI && (
              <div className="col-span-2 text-center">
                 <button 
                  onClick={() => setShowAoAInRSSI(false)}
                  className="px-2 py-1 text-xs bg-rf-card border border-rf-red text-rf-red rounded hover:bg-rf-red hover:text-white transition-colors w-full"
                >
                  HIDE PEEK AoA
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
